import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import { Stack, Typography, alpha } from '@mui/material';
import { LinkedinTypes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { openSupport } from 'utils/support.utils';
import { AppSyncErrors } from '../app-sync-dialog.types';
import StepActions from '../step-actions';
import StepHint from '../step-hint';
import StepLayout from '../step-layout';

interface Props {
  liProductType: LinkedinTypes;
  loading: boolean;
  syncError: AppSyncErrors | null;

  userName: string;
  password: string;

  /** Kurzzeichen des Kontos, das der Support braucht: „#A7F3-9C · Claudia Künzl". */
  referenceLabel?: string;

  handleUserNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleEditAndSynchronizeLiAccount(): void;

  handleReSynchronizeLiAccount(): void;
}

/**
 * Ein Fehler, ein Satz, genau eine Aktion.
 *
 * Vorher stand links eine allgemeine Fehlerseite („Wir haben ein Problem festgestellt")
 * und rechts eine zweite mit der eigentlichen Auskunft — derselbe Fehler zweimal
 * beschrieben, dazwischen ein Trenner. Klare Fehler tragen jetzt ihre Lösung direkt,
 * und der unklare Fall führt zum Support statt zu einem Formular.
 */
const SyncError: React.FC<Props> = ({
  liProductType,
  loading,
  syncError,
  userName,
  password,
  referenceLabel,
  handleUserNameChange,
  handlePasswordChange,
  handleEditAndSynchronizeLiAccount,
  handleReSynchronizeLiAccount,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const liProductTypeText =
    liProductType === LinkedinTypes.RECRUITER ? 'Recruiter' : liProductType === LinkedinTypes.SALES_NAV ? 'Sales Navigator' : 'LinkedIn';

  const twoFaUrl =
    liProductType === LinkedinTypes.RECRUITER
      ? 'https://www.linkedin.com/talent/two-step-verification'
      : 'https://www.linkedin.com/mypreferences/d/two-factor-authentication';

  // Opens our own feedback drawer instead of app.loopedin.io (176ce751). Die Referenz geht
  // mit raus — ohne sie muss der Support das Konto erst suchen.
  const handleContactSupport = () =>
    openSupport({
      title: t('syncErrorSupportTitle', 'LinkedIn-Synchronisation fehlgeschlagen'),
      description: t('syncErrorSupportDescription', 'Fehler bei der Synchronisation: {{reason}}', {
        reason: `${syncError ?? 'unbekannt'}${referenceLabel ? ` · ${referenceLabel}` : ''}`,
      }),
    });

  const isWrongCredentials = syncError === AppSyncErrors.WRONG_CREDENTIALS;
  const isUnknown = _.isNil(syncError) || syncError === AppSyncErrors.UNKNOWN;

  const createScreen = () => {
    switch (syncError) {
      case AppSyncErrors.WRONG_CREDENTIALS:
        return {
          tone: 'error' as const,
          icon: <LockResetIcon />,
          title: t('appSyncErrorWrongPasswordTitle', 'Das Passwort stimmt nicht'),
          body: t(
            'appSyncErrorWrongPasswordText',
            'LinkedIn hat die Anmeldung abgelehnt. Prüfe die Zugangsdaten und versuche es noch einmal — dein Konto ist nicht gesperrt.',
          ),
          primaryText: t('appSyncErrorSignInAgain', 'Erneut anmelden'),
          onPrimary: handleEditAndSynchronizeLiAccount,
        };
      case AppSyncErrors.WRONG_PIN:
        return {
          tone: 'error' as const,
          icon: <PhonelinkLockIcon />,
          title: t('appSyncErrorWrongPinTitle', 'Der Code wurde abgelehnt'),
          body: t('syncErrorWrongPin'),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      case AppSyncErrors.TIMED_OUT_CAPTCHA:
        return {
          tone: 'error' as const,
          icon: <TimerOffIcon />,
          title: t('appSyncErrorCaptchaTitle', 'Die Zeit ist abgelaufen'),
          body: t('syncErrorTimedOutCaptcha'),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      case AppSyncErrors.ONBOARDING_INCOMPLETE:
        return {
          tone: 'error' as const,
          icon: <ReportProblemOutlinedIcon />,
          title: t('appSyncErrorOnboardingTitle', 'Das LinkedIn-Onboarding ist nicht abgeschlossen'),
          body: (
            <>
              {t('syncErrorLinkedinOnboardingIncompleteText1')}{' '}
              {t('syncErrorLinkedinOnboardingIncompleteText3')}
            </>
          ),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      case AppSyncErrors.TWO_FA_NOT_ENABLED:
        return {
          tone: 'error' as const,
          icon: <PhonelinkLockIcon />,
          title: t('appSyncErrorTwoFaTitle', 'Zwei-Faktor-Bestätigung für {{liProductType}} aktivieren', { liProductType: liProductTypeText }),
          body: (
            <>
              {t('syncErrorTwoFaNotEnabledText1', { liProductType: liProductTypeText })}{' '}
              <Trans i18nKey={'syncErrorTwoFaNotEnabledText2'} values={{ liProductType: liProductTypeText }}>
                Please
                <a className={classes.link} href={twoFaUrl} target={'_blank'} rel={'noreferrer'}>
                  activate 2FA in your {liProductTypeText} account
                </a>
                .
              </Trans>
            </>
          ),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      case AppSyncErrors.LOGIN_EXPIRED:
        return {
          tone: 'error' as const,
          icon: <TimerOffIcon />,
          title: t('appSyncErrorLoginExpiredTitle', 'Die Anmeldung ist abgelaufen'),
          body: t(
            'syncErrorLoginExpiredText',
            'The previous login attempt timed out or was not completed in time. This can happen when a two-factor authentication code expires or a push notification is not approved.',
          ),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      case AppSyncErrors.CONNECTIVITY_COOLDOWN:
        return {
          tone: 'error' as const,
          icon: <TimerOffIcon />,
          title: t('connectivityCooldownActive', 'Connectivity Cooldown Active'),
          body: t(
            'syncErrorConnectivityCooldownText',
            'Repeated network issues were detected for this account. Automatic retries are temporarily paused to protect account safety.',
          ),
          primaryText: t('retryAfterCooldown', 'Retry Synchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
      default:
        return {
          tone: 'neutral' as const,
          icon: <HelpOutlineIcon />,
          title: t('appSyncErrorUnknownTitle', 'Wir kümmern uns darum'),
          body: t(
            'appSyncErrorUnknownText',
            'Bei der Anmeldung ist etwas Unerwartetes passiert. Der Fehler ist mit allen Details protokolliert — melde dich beim Support, dann sehen wir uns das an.',
          ),
          primaryText: t('retrySynchronization'),
          onPrimary: handleReSynchronizeLiAccount,
        };
    }
  };

  const screen = createScreen();

  return (
    <StepLayout
      title={screen.title}
      description={screen.body}
      footer={
        <StepActions
          primaryText={screen.primaryText}
          primaryLoading={isWrongCredentials ? loading : undefined}
          primaryDisabled={isWrongCredentials ? !userName || !password : undefined}
          onPrimary={screen.onPrimary}
        />
      }
    >
      <Stack gap={2}>
        <div className={cx(classes.iconTile, screen.tone)}>{screen.icon}</div>

        {isWrongCredentials ? (
          <Stack gap={1.5}>
            <PaiTextField
              value={userName}
              placeholder={`${t('email')} (LinkedIn)`}
              autocomplete={'one-time-code'}
              name={'linkedin-email'}
              onChange={handleUserNameChange}
            />
            <PaiTextField
              value={password}
              placeholder={`${t('password')} (LinkedIn)`}
              autocomplete={'new-password'}
              name={'linkedin-password'}
              type={'password'}
              error={true}
              errorText={t('appSyncErrorWrongPasswordFieldHint', 'Bitte gib das aktuelle LinkedIn-Passwort ein.')}
              onChange={handlePasswordChange}
            />
          </Stack>
        ) : null}

        {isUnknown ? (
          <>
            <StepHint icon={<RefreshIcon />} text={t('appSyncErrorUnknownRetryHint', 'Wir versuchen es in Kürze automatisch erneut.')} />
            {!_.isNil(referenceLabel) ? (
              <Stack className={classes.referenceBox} gap={0.25}>
                <Typography className={classes.referenceLabel}>{t('appSyncErrorReference', 'Referenz')}</Typography>
                <Typography className={classes.referenceValue}>{referenceLabel}</Typography>
              </Stack>
            ) : null}
          </>
        ) : null}

        <Stack direction={'row'}>
          <PaiLink text={t('support')} size={'md'} underline={'always'} action={handleContactSupport} />
        </Stack>
      </Stack>
    </StepLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  iconTile: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: theme.typography.pxToRem(26),
    },
    '&.error': {
      background: alpha(theme.palette.error.main, 0.09),
      '& svg': {
        color: theme.palette.error.main,
      },
    },
    '&.neutral': {
      background: theme.palette.backgrounds.main,
      '& svg': {
        color: theme.palette.primary.main,
      },
    },
  },
  referenceBox: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '10px',
    padding: '13px 15px',
  },
  referenceLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
  },
  referenceValue: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    letterSpacing: '0.3px',
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

export default SyncError;
